import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Paper,
  Button,
  Select, 
  MenuItem, 
  FormControl,
  Switch, 
  FormControlLabel
} from "@material-ui/core";
import { API } from "aws-amplify";
import { getProductWithoutCustomers, queryCustomersByProductID } from "../../graphql/queries";
import { getRecaptchaToken } from '../../utils/recaptcha';
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import { SiLine } from "react-icons/si";
import { Pagination } from "@material-ui/lab";

const ProductDashboard = () => {
  const { productID } = useParams();
  const history = useHistory();

  const [product, setProduct] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showOnlyWithInquiry, setShowOnlyWithInquiry] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productRes, customerRes] = await Promise.all([
          API.graphql({
            query: getProductWithoutCustomers,
            variables: { id: productID },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          }),
          API.graphql({
            query: queryCustomersByProductID,
            variables: { productID, limit: 1000 },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          })
        ]);

        const fetchedProduct = productRes?.data?.getProduct;
        const fetchedCustomers = customerRes?.data?.queryCustomersByProductID?.items || [];

        if (fetchedProduct) {
          setProduct(fetchedProduct);
          setCustomers(fetchedCustomers);
        } else {
          setError("商品が見つかりませんでした。");
        }
      } catch (err) {
        console.error("データ取得失敗:", err);
        setError("データの取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [productID]);

  useEffect(() => {
    const initRecaptcha = async () => {
      const token = await getRecaptchaToken('dashboard_product');
      setToken(token);
    };
    initRecaptcha();
    const interval = setInterval(initRecaptcha, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><CircularProgress /></Box>;
  if (error) return <Box m={4}><Typography variant="h6" color="error">{error}</Typography></Box>;
  if (!product) return null;

  const salesCount = customers.length;
  const totalRevenue = (product.price ?? 0) * salesCount;

  const shareMessage = `ピッタプットで申し込み受付中！ ${product.title ?? ""} ${product.URL ?? ""} #ピッタプット`;
  const encodedMessage = encodeURIComponent(shareMessage);

  const filteredCustomers = showOnlyWithInquiry
    ? customers.filter(c => !!c.refundComment?.trim())
    : customers;

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const timeA = Number(a.created_at) || 0;
    const timeB = Number(b.created_at) || 0;
    return timeB - timeA;
  });

  const paginatedCustomers = sortedCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return "不明";
    const date = new Date(Number(timestamp) * 1000);
    if (isNaN(date.getTime())) return "不明";
    return date.toLocaleString("ja-JP");
  };  

  const handleDownloadCSV = () => {
    // CSVヘッダー
    const headers = [
      "名前",
      "購入日時",
      "メールアドレス",
      "備考欄",
      "お問い合わせ内容",
      "対応状況"
    ].join(",");

    // テキストをCSV用に整形する関数
    const escapeCSV = (text) => {
      if (!text) return '""';
      // カンマ、改行、ダブルクォートを含む場合はダブルクォートで囲む
      const escaped = text.replace(/"/g, '""'); // ダブルクォートをエスケープ
      const needsQuotes = /[",\n\r]/.test(text);
      return needsQuotes ? `"${escaped}"` : escaped;
    };

    const csvData = sortedCustomers.map(customer => {
      const purchaseDate = formatDate(customer.created_at);
      const hasInquiry = customer.refundComment?.trim() ? "あり" : "なし";
      const status = customer.answer ? "回答済み" : (hasInquiry === "あり" ? "未対応" : "-");
      
      return [
        escapeCSV(customer.name || "未入力"),
        escapeCSV(purchaseDate),
        escapeCSV(customer.email || "未入力"),
        escapeCSV(customer.note || "未記入"),
        escapeCSV(customer.refundComment || ""),
        escapeCSV(status)
      ].join(",");
    });

    // CSVの作成
    const csv = [headers, ...csvData].join("\n");
    
    // BOMを付与してUTF-8でエンコード
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, csv], { type: "text/csv;charset=utf-8" });
    
    // ダウンロード
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `customers_${product.title}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Box m={4}>
      <Paper style={{ padding: 16 }}>
        <Typography variant="h6" gutterBottom>商品名: {product.title}</Typography>
        <Typography variant="body1">
          商品URL: <a href={`${window.location.origin}/products/${product.id}`} target="_blank" rel="noopener noreferrer">
            {`${window.location.origin}/products/${product.id}`}
          </a>
        </Typography>
        <Typography variant="body1">価格: {product.price ? `${product.price.toLocaleString()} 円` : "価格が登録されていません"}</Typography>
        <Typography variant="body1">商品説明: {product.description ?? "説明が登録されていません"}</Typography>
        <Typography variant="body1">販売数: {salesCount}</Typography>
        <Typography variant="body1">売上(円): {totalRevenue.toLocaleString()}</Typography>

        <Box mt={2}>
          <Button variant="contained" color="primary" onClick={() => history.push("/dashboard")} style={{ marginRight: 8 }}>戻る</Button>
          <Button variant="contained" startIcon={<FaFacebookF />} onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(product.URL ?? "")}&quote=${encodedMessage}`, "_blank")} style={{ backgroundColor: "#1877F2", color: "#fff", marginRight: 8 }}>Facebookで共有</Button>
          <Button variant="contained" startIcon={<FaTwitter />} onClick={() => window.open(`https://x.com/intent/tweet?text=${encodedMessage}`, "_blank")} style={{ backgroundColor: "#1DA1F2", color: "#fff", marginRight: 8 }}>Xで共有</Button>
          <Button variant="contained" startIcon={<SiLine />} onClick={() => window.open(`https://social-plugins.line.me/lineit/share?text=${encodedMessage}`, "_blank")} style={{ backgroundColor: "#00C300", color: "#fff" }}>LINEで共有</Button>
        </Box>
      </Paper>
      {customers.length > 0 && (
      <Paper style={{ padding: 16, marginTop: 32 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">顧客一覧</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadCSV}
            disabled={!filteredCustomers.length}
          >
            CSVダウンロード
          </Button>
        </Box>
        <TableContainer>
        <Box mt={3} display="flex" alignItems="center" gap={2}>
          <Typography variant="body1">表示件数：</Typography>
          <FormControl variant="outlined" size="small">
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setCurrentPage(1);
                const newLimit = Number(e.target.value);
                setItemsPerPage(newLimit);
              }}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>{n} 件</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mb={2}>
          <FormControlLabel
            control={
              <Switch
              checked={showOnlyWithInquiry}
              onChange={() => setShowOnlyWithInquiry(prev => !prev)}
                color="primary"
              />
            }
            label="お問い合わせされた方のみ表示"
          />
        </Box>
          <Table>
          <TableHead>
            <TableRow>
              <TableCell>名前</TableCell>
              <TableCell>購入日時</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>備考欄</TableCell>
              {showOnlyWithInquiry && (
                <>
                  <TableCell>お問い合わせ内容</TableCell>
                  <TableCell>対応</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCustomers.map((customer, idx) => (
              <TableRow key={idx}>
                <TableCell>{customer.name ?? "未入力"}</TableCell>
                <TableCell>{formatDate(customer.created_at)}</TableCell>
                <TableCell>{customer.email ?? "未入力"}</TableCell>
                <TableCell>{customer.note ?? "未記入"}</TableCell>

                {showOnlyWithInquiry && (
                  <>
                    <TableCell>
                      <Typography variant="body2" style={{ color: "red", fontWeight: "bold" }}>
                        お問い合わせが届いています
                      </Typography>
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                        {customer.refundComment}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        style={{ marginRight: 8 }}
                        onClick={() => console.log("キャンセル承認:", customer.id)}
                      >
                        キャンセル承認
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => console.log("回答:", customer.id)}
                      >
                        回答する
                      </Button>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => setCurrentPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      </Paper>
    )}
    </Box>
  );
};

export default ProductDashboard;